import { FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Fieldset, Input, Label, Radio, ValidationMessage } from '@digdir/designsystemet-react';
import type { NewQuestion } from '../riskFactorsView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskFactors.module.css';

type Props = {
  id: string;
  /** Adding shows help texts and examples. Editing is compact. */
  variant: 'add' | 'edit';
  initial?: NewQuestion;
  saving: boolean;
  onSubmit: (question: NewQuestion) => void;
  onCancel: () => void;
};

export const QuestionForm = ({ id, variant, initial, saving, onSubmit, onCancel }: Props): ReactElement => {
  const { t } = useTranslation();
  const [question, setQuestion] = useState<NewQuestion>(initial ?? { text: '', highIsGood: true });
  const [tried, setTried] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const adding = variant === 'add';
  const textError = tried && !question.text.trim();

  useEffect(() => inputRef.current?.focus(), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!question.text.trim()) {
      setTried(true);
      return;
    }
    onSubmit({ ...question, text: question.text.trim() });
  };

  return (
    <form onSubmit={submit} noValidate className={adding ? classes.addQuestion : classes.editQuestion}>
      <Field data-size="sm">
        <Label htmlFor={id}>{t('hrRiskFactors.question.text')}</Label>
        {adding && (
          <Field.Description id={`${id}-description`}>{t('hrRiskFactors.question.textDescription')}</Field.Description>
        )}
        <Input
          ref={inputRef}
          id={id}
          aria-describedby={adding ? `${id}-description` : undefined}
          aria-invalid={textError || undefined}
          value={question.text}
          onChange={(event) => setQuestion({ ...question, text: event.target.value })}
        />
        {textError && <ValidationMessage>{t('hrRiskFactors.question.textError')}</ValidationMessage>}
      </Field>
      <Fieldset data-size="sm" className={adding ? classes.directionStack : classes.directionInline}>
        <Fieldset.Legend>{t('hrRiskFactors.question.direction')}</Fieldset.Legend>
        {adding && <Fieldset.Description>{t('hrRiskFactors.question.directionDescription')}</Fieldset.Description>}
        <Radio
          name={`${id}-direction`}
          value="good"
          label={t(adding ? 'hrRiskFactors.question.goodExample' : 'hrRiskFactors.question.good')}
          checked={question.highIsGood}
          onChange={() => setQuestion({ ...question, highIsGood: true })}
        />
        <Radio
          name={`${id}-direction`}
          value="problem"
          label={t(adding ? 'hrRiskFactors.question.problemExample' : 'hrRiskFactors.question.problem')}
          checked={!question.highIsGood}
          onChange={() => setQuestion({ ...question, highIsGood: false })}
        />
      </Fieldset>
      <div className={layout.actions} data-gap="sm">
        <Button type="submit" variant="primary" data-size="sm" loading={saving}>
          {t(adding ? 'hrRiskFactors.question.add' : 'hrRiskFactors.question.save')}
        </Button>
        <Button type="button" variant="secondary" data-size="sm" onClick={onCancel}>
          {t('hrRiskFactors.cancel')}
        </Button>
      </div>
    </form>
  );
};
